import React, { useState, useEffect, useCallback } from 'react';
import { reportService, supermercadoService, orderService, extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';

function OrdersBySupermercadoReport() {
  const [orders, setOrders] = useState([]);
  const [supermercados, setSupermercados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [historyData, setHistoryData] = useState({});
  const [loadingHistory, setLoadingHistory] = useState({});
  const [showConfirmedOrder, setShowConfirmedOrder] = useState({});
  
  const [filters, setFilters] = useState({
    supermercadoId: '',
    status: '',
    fromDate: '',
    toDate: '',
    sortBy: 'createdAt',
    sortDirection: 'desc'
  });
  
  const [pagination, setPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0
  });

  useEffect(() => {
    const fetchSupermercados = async () => {
      try {
        const response = await supermercadoService.getAllSupermercados();
        if (response.success) {
          setSupermercados(response.data || []);
        }
      } catch (err) {
        console.error('Error loading supermercados:', err);
      }
    };
    fetchSupermercados();
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      setDebugInfo(null);
      
      const filterParams = {
        ...filters,
        page: pagination.page,
        size: 20
      };
      
      // Clean empty values
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] === '') {
          delete filterParams[key];
        }
      });
      
      const response = await reportService.getOrdersBySupermercadoReport(filterParams);
      if (response.success) {
        const data = response.data;
        setOrders(data.content || []);
        setPagination({
          page: data.pageNumber,
          totalPages: data.totalPages,
          totalElements: data.totalElements
        });
      } else {
        setError(response.message || 'Error al cargar el reporte');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al cargar el reporte de órdenes');
      setError(message);
      setErrorTrace(trace);
      setDebugInfo(debug);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setFilters({
      supermercadoId: '',
      status: '',
      fromDate: '',
      toDate: '',
      sortBy: 'createdAt',
      sortDirection: 'desc'
    });
    setPagination({ page: 0, totalPages: 0, totalElements: 0 });
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-UY', { timeZone: 'America/Montevideo' });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PENDIENTE': return 'Pendiente';
      case 'EN_PROCESO': return 'En Proceso';
      case 'ENVIADA': return 'Enviada';
      case 'RECIBIDA': return 'Recibida';
      default: return status;
    }
  };

  const toggleExpand = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const loadHistory = async (orderId) => {
    if (historyData[orderId]) return;
    
    setLoadingHistory(prev => ({ ...prev, [orderId]: true }));
    try {
      const response = await orderService.getOrderHistory(orderId);
      if (response.success) {
        setHistoryData(prev => ({ ...prev, [orderId]: response.data || [] }));
      }
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setLoadingHistory(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const toggleHistory = (orderId) => {
    if (!historyData[orderId]) {
      loadHistory(orderId);
    }
  };

  const toggleConfirmedOrder = (orderId) => {
    setShowConfirmedOrder(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const hasConfirmedQuantities = (order) => {
    return order.items && order.items.some(item => item.cantidadConfirmada !== null && item.cantidadConfirmada !== undefined);
  };

  const getTotalItems = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => sum + item.cantidad, 0);
  };

  const getTotalConfirmedItems = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => sum + (item.cantidadConfirmada || 0), 0);
  };

  const getDiffClassName = (diff) => {
    if (diff < 0) return 'diff-negative';
    if (diff > 0) return 'diff-positive';
    return '';
  };

  const formatDiff = (diff) => {
    return diff > 0 ? `+${diff}` : diff.toString();
  };

  return (
    <div className="report-container">
      <h2>Reporte de Órdenes por Supermercado</h2>
      
      <div className="report-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="supermercadoId">Supermercado</label>
            <select
              id="supermercadoId"
              value={filters.supermercadoId}
              onChange={(e) => handleFilterChange('supermercadoId', e.target.value)}
            >
              <option value="">Todos</option>
              {supermercados.map(sup => (
                <option key={sup.id} value={sup.id}>{sup.nombre}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="status">Estado</label>
            <select
              id="status"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">Todos</option>
              <option value="PENDIENTE">Pendiente</option>
              <option value="EN_PROCESO">En Proceso</option>
              <option value="ENVIADA">Enviada</option>
              <option value="RECIBIDA">Recibida</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="fromDate">Desde</label>
            <input
              type="date"
              id="fromDate"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <label htmlFor="toDate">Hasta</label>
            <input
              type="date"
              id="toDate"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
            />
          </div>
        </div>
        
        <div className="filter-actions">
          <button onClick={handleApplyFilters} className="btn btn-primary">
            Aplicar Filtros
          </button>
          <button onClick={handleClearFilters} className="btn btn-secondary">
            Limpiar Filtros
          </button>
        </div>
        <div className="export-buttons" style={{ marginTop: '10px' }}>
          <button
            onClick={async () => {
              try {
                setError('');
                setErrorTrace(null);
                setDebugInfo(null);
                await reportService.exportOrdersBySupermercadoToPdf(filters);
              } catch (err) {
                console.error('Error exporting to PDF:', err);
                const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al exportar a PDF');
                setError(message);
                setErrorTrace(trace);
                setDebugInfo(debug);
              }
            }}
            className="btn btn-small btn-primary"
            style={{ marginRight: '10px' }}
          >
            📄 Exportar a PDF
          </button>
          <button
            onClick={async () => {
              try {
                setError('');
                setErrorTrace(null);
                setDebugInfo(null);
                await reportService.exportOrdersBySupermercadoToExcel(filters);
              } catch (err) {
                console.error('Error exporting to Excel:', err);
                const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al exportar a Excel');
                setError(message);
                setErrorTrace(trace);
                setDebugInfo(debug);
              }
            }}
            className="btn btn-small btn-success"
            style={{ marginRight: '10px' }}
          >
            📊 Exportar a Excel
          </button>
          <button
            onClick={async () => {
              try {
                setError('');
                setErrorTrace(null);
                setDebugInfo(null);
                await reportService.exportOrdersBySupermercadoToCsv(filters);
              } catch (err) {
                console.error('Error exporting to CSV:', err);
                const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al exportar a CSV');
                setError(message);
                setErrorTrace(trace);
                setDebugInfo(debug);
              }
            }}
            className="btn btn-small btn-info"
          >
            📋 Exportar a CSV
          </button>
        </div>
      </div>

      <ErrorDisplay error={error} errorTrace={errorTrace} debugInfo={debugInfo} />

      {loading ? (
        <p className="loading-text">Cargando órdenes...</p>
      ) : (
        <>
          <div className="report-summary">
            <p>Total de órdenes encontradas: {pagination.totalElements}</p>
          </div>

          {orders.length === 0 ? (
            <div className="empty-state">
              <p>No hay órdenes que coincidan con los criterios de búsqueda</p>
            </div>
          ) : (
            <div className="orders-list">
              {orders.map((order) => (
                <div key={order.id} className="order-card">
                  <div className="order-header" onClick={() => toggleExpand(order.id)}>
                    <div className="order-main-info">
                      <span className="order-id">Orden #{order.id}</span>
                      <span className={`status-badge status-${order.status}`}>
                        {getStatusLabel(order.status)}
                      </span>
                    </div>
                    <div className="order-summary">
                      <span className="order-items-count">
                        {order.items?.length || 0} producto(s) - {getTotalItems(order.items)} unidad(es)
                      </span>
                      <span className="expand-icon">{expandedOrders[order.id] ? '▼' : '▶'}</span>
                    </div>
                  </div>
                  
                  <div className="order-meta">
                    {order.supermercadoNombre && (
                      <span><strong>Supermercado:</strong> {order.supermercadoNombre}</span>
                    )}
                    <span>Creado por: {order.createdByNombre || order.createdByUsername}</span>
                    <span>Creado: {formatDate(order.createdAt)}</span>
                  </div>
                  
                  {order.statusChangedAt && (
                    <div className="order-status-date">
                      <span>Estado actualizado: {formatDate(order.statusChangedAt)}</span>
                    </div>
                  )}
                  
                  <div className="order-description">
                    <strong>Descripción:</strong> {order.descripcion}
                  </div>
                  
                  <div className="order-actions">
                    <button 
                      onClick={async () => {
                        try {
                          await orderService.downloadOrderPdf(order.id);
                        } catch (err) {
                          console.error('Error downloading PDF:', err);
                          alert('Error al descargar el PDF: ' + (err.message || err));
                        }
                      }} 
                      className="btn btn-small btn-primary"
                      style={{ marginRight: '10px' }}
                    >
                      📄 Descargar PDF
                    </button>
                  </div>

                  {expandedOrders[order.id] && order.items && order.items.length > 0 && (
                    <div className="order-items-detail">
                      <table className="items-table">
                        <thead>
                          <tr>
                            <th>Código</th>
                            <th>Producto</th>
                            <th>Cantidad</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.items.map((item, index) => (
                            <tr key={index}>
                              <td>{item.codigoBarras}</td>
                              <td>{item.nombreProducto}</td>
                              <td>{item.cantidad}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Confirmed Order Document */}
                  {(order.status === 'ENVIADA' || order.status === 'RECIBIDA') && hasConfirmedQuantities(order) && (
                    <div className="confirmed-order-section">
                      <button 
                        onClick={() => toggleConfirmedOrder(order.id)} 
                        className="btn btn-small btn-info"
                      >
                        {showConfirmedOrder[order.id] ? 'Ocultar Orden Confirmada' : 'Ver Orden Confirmada'}
                      </button>
                      
                      {showConfirmedOrder[order.id] && (
                        <div className="confirmed-order-document">
                          <h4>📋 Documento de Orden Confirmada</h4>
                          <p className="confirmed-order-subtitle">Cantidades reales enviadas por el depósito</p>
                          <table className="confirmed-items-table">
                            <thead>
                              <tr>
                                <th>Código</th>
                                <th>Producto</th>
                                <th>Cant. Pedida</th>
                                <th>Cant. Enviada</th>
                                <th>Diferencia</th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.items.map((item, index) => {
                                const diff = (item.cantidadConfirmada || 0) - item.cantidad;
                                return (
                                  <tr key={index} className={diff !== 0 ? 'quantity-diff' : ''}>
                                    <td>{item.codigoBarras}</td>
                                    <td>{item.nombreProducto}</td>
                                    <td>{item.cantidad}</td>
                                    <td><strong>{item.cantidadConfirmada}</strong></td>
                                    <td className={getDiffClassName(diff)}>
                                      {formatDiff(diff)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              {(() => {
                                const totalDiff = getTotalConfirmedItems(order.items) - getTotalItems(order.items);
                                return (
                                  <tr>
                                    <td colSpan="2"><strong>Total</strong></td>
                                    <td><strong>{getTotalItems(order.items)}</strong></td>
                                    <td><strong>{getTotalConfirmedItems(order.items)}</strong></td>
                                    <td className={getDiffClassName(totalDiff)}>
                                      <strong>{formatDiff(totalDiff)}</strong>
                                    </td>
                                  </tr>
                                );
                              })()}
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Status History Section */}
                  {expandedOrders[order.id] && (
                    <div className="order-history-section">
                      <button 
                        onClick={() => toggleHistory(order.id)} 
                        className="btn btn-small btn-secondary"
                      >
                        {historyData[order.id] ? 'Historial de Estados' : 'Ver Historial de Estados'}
                      </button>
                      
                      {loadingHistory[order.id] && <p>Cargando historial...</p>}
                      
                      {historyData[order.id] && historyData[order.id].length > 0 && (
                        <div className="history-list">
                          <h4>Historial de Cambios de Estado</h4>
                          <table className="history-table">
                            <thead>
                              <tr>
                                <th>Fecha</th>
                                <th>Estado Anterior</th>
                                <th>Nuevo Estado</th>
                                <th>Usuario</th>
                              </tr>
                            </thead>
                            <tbody>
                              {historyData[order.id].map((history, index) => (
                                <tr key={index}>
                                  <td>{formatDate(history.changedAt)}</td>
                                  <td>{getStatusLabel(history.previousStatus)}</td>
                                  <td>{getStatusLabel(history.newStatus)}</td>
                                  <td>{history.changedByNombre} ({history.changedByUsername})</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      
                      {historyData[order.id] && historyData[order.id].length === 0 && (
                        <p className="no-history">No hay cambios de estado registrados</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 0}
                className="btn btn-small"
              >
                Anterior
              </button>
              <span>Página {pagination.page + 1} de {pagination.totalPages}</span>
              <button 
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
                className="btn btn-small"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default OrdersBySupermercadoReport;
